import PDFPage from './PDFPage'
import type { ReportData, BrandingConfig } from '@/types'
import { formatCurrency } from '@/utils/calculations'

interface YearByYearPageProps {
  data: ReportData
  branding: BrandingConfig
  pageNumber: number
  totalPages: number
}

export default function YearByYearPage({
  data,
  branding,
  pageNumber,
  totalPages,
}: YearByYearPageProps) {
  const isLight = branding.theme === 'light'
  const cardBg = isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)'
  const cardBorder = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'
  const headerBg = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)'

  return (
    <PDFPage
      pageNumber={pageNumber}
      totalPages={totalPages}
      title="Year-by-Year Breakdown"
      subtitle="Detailed Projections"
      branding={branding}
    >
      {/* Table */}
      <div
        style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
          overflow: 'hidden',
          marginBottom: '24px',
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 1fr 1fr',
            gap: '12px',
            padding: '12px 16px',
            background: headerBg,
            fontWeight: 600,
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          <div>Year</div>
          <div>With Advice</div>
          <div>Without Advice</div>
          <div style={{ color: branding.primaryColor }}>Value Added</div>
        </div>

        {/* Table Body */}
        {data.timeline.map((point, index) => (
          <div
            key={point.year}
            style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr 1fr 1fr',
              gap: '12px',
              padding: '10px 16px',
              borderTop: index > 0 ? `1px solid ${cardBorder}` : 'none',
              fontSize: '12px',
              background:
                point.year === data.years
                  ? `linear-gradient(90deg, ${branding.primaryColor}10 0%, transparent 100%)`
                  : 'transparent',
            }}
          >
            <div style={{ fontWeight: point.year === data.years ? 600 : 400 }}>
              {point.year}
            </div>
            <div>{formatCurrency(point.withAdvice)}</div>
            <div style={{ opacity: 0.7 }}>{formatCurrency(point.withoutAdvice)}</div>
            <div style={{ color: branding.primaryColor, fontWeight: 500 }}>
              +{formatCurrency(point.valueAdded)}
            </div>
          </div>
        ))}
      </div>

      {/* Summary Row */}
      <div
        style={{
          background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
          borderRadius: '12px',
          padding: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '20px',
          color: '#1a1f2e',
          marginBottom: '24px',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>
            Final Portfolio (With Advice)
          </div>
          <div style={{ fontSize: '24px', fontWeight: 600 }}>
            {formatCurrency(data.results.withAdvice)}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>
            Final Portfolio (Without)
          </div>
          <div style={{ fontSize: '24px', fontWeight: 600 }}>
            {formatCurrency(data.results.withoutAdvice)}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>
            Total Value of Advice
          </div>
          <div style={{ fontSize: '24px', fontWeight: 600 }}>
            {formatCurrency(data.results.valueAdded)}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div
        style={{
          padding: '16px',
          background: isLight ? '#fef3c7' : 'rgba(245, 158, 11, 0.1)',
          border: `1px solid ${isLight ? '#fcd34d' : 'rgba(245, 158, 11, 0.3)'}`,
          borderRadius: '8px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f59e0b"
            strokeWidth="2"
            style={{ flexShrink: 0, marginTop: '2px' }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <div style={{ fontWeight: 600, fontSize: '12px', marginBottom: '4px', color: '#f59e0b' }}>
              Important Information
            </div>
            <p style={{ fontSize: '11px', opacity: 0.8, margin: 0, lineHeight: 1.5 }}>
              These projections are for illustrative purposes only and are based on historical data
              and research. Past performance is not a guarantee of future results. Actual returns
              may vary based on market conditions, investment choices, and individual circumstances.
              The value of investments can go down as well as up.
            </p>
          </div>
        </div>
      </div>
    </PDFPage>
  )
}
